import { useState, useEffect } from 'react'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Badge, ScrollArea } from '@creanizer/ui'
import { Search, Trash2, Eye, Edit, Skull, Download, Folder, RefreshCw } from 'lucide-react'
import { useEnemyStore } from '../../../../stores/enemyStore'
import { useFolderStore } from '../../../../stores/folderStore'
import type { SavedEnemy, EnemyInput, EnemyResult } from '@creanizer/types'
import { ExportImportButtons } from '../../../../components/common'
import { EnemyEditModal } from './EnemyEditModal'
import { FolderManager, FolderMoveSelect } from './FolderManager'

interface SavedEnemiesListProps {
  onLoad: (enemy: { input: EnemyInput; result: EnemyResult }) => void
  onPartialRegenerate?: (enemy: { input: EnemyInput; result: EnemyResult }) => void
}

export function SavedEnemiesList({ onLoad, onPartialRegenerate }: SavedEnemiesListProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [editingEnemy, setEditingEnemy] = useState<SavedEnemy | null>(null)
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null) // デフォルトを未整理に変更
  const [showFolders, setShowFolders] = useState(true)
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null)
  
  const { enemies, removeEnemy, clearEnemies, exportAsText, exportAsJSON, exportSingleAsText, exportSingleAsJSON, importFromJSON, fixDuplicateIds, updateEnemy } = useEnemyStore()
  const { getItemsInFolder, getItemFolder, addItemToFolder, moveItemBetweenFolders } = useFolderStore()
  
  // コンポーネントマウント時に重複IDをチェックして修正
  useEffect(() => {
    const hasDuplicates = fixDuplicateIds()
    if (hasDuplicates) {
      console.log('エネミーIDの重複を修正しました')
    }
  }, [fixDuplicateIds])

  // フォルダによるフィルタリング
  let folderFilteredEnemies = enemies
  if (selectedFolderId === 'all') {
    // すべてのエネミーを表示
    folderFilteredEnemies = enemies
  } else if (selectedFolderId === null) {
    // 未整理のアイテムのみ: どのフォルダにも属していないアイテム
    const allFolderItemIds = new Set<string>()
    const folders = useFolderStore.getState().getFolders('enemy')
    folders.forEach(folder => {
      getItemsInFolder('enemy', folder.id).forEach(id => allFolderItemIds.add(id))
    })
    folderFilteredEnemies = enemies.filter(enemy => !allFolderItemIds.has(enemy.id))
  } else {
    // 特定のフォルダのアイテムのみ
    const itemIds = getItemsInFolder('enemy', selectedFolderId)
    folderFilteredEnemies = enemies.filter(enemy => itemIds.includes(enemy.id))
  }
  
  // 検索によるフィルタリング
  const filteredEnemies = folderFilteredEnemies.filter(enemy =>
    enemy.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    enemy.result.basicInfo.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    enemy.result.basicInfo.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (enemy.tags && enemy.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
  )
  
  // 新規作成されたエネミーを自動的に未整理フォルダに追加
  useEffect(() => {
    enemies.forEach(enemy => {
      const folder = getItemFolder('enemy', enemy.id)
      if (!folder) {
        // フォルダに属していない場合は未整理として扱う（既に処理済み）
      }
    })
  }, [enemies, getItemFolder])

  return (
    <div className="card max-w-full overflow-x-hidden">
      <div className="flex items-center gap-2 mb-2">
        <Skull className="h-5 w-5" />
        <h2 className="text-xl font-semibold">
          保存済みエネミー ({enemies.length}/500)
        </h2>
      </div>
      <p className="text-muted-foreground mb-6">
        生成したエネミー・怪異設定を保存・管理
      </p>
      
      <div className="space-y-4 max-w-full">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="エネミーを検索..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFolders(!showFolders)}
            className="px-2"
          >
            <Folder className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex gap-2">
          <ExportImportButtons
            onExportText={exportAsText}
            onExportJSON={exportAsJSON}
            onImportJSON={importFromJSON}
            dataTypeName="エネミー"
          />

          {enemies.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (confirm('すべてのエネミーを削除しますか？')) {
                  clearEnemies()
                }
              }}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              全削除
            </Button>
          )}
        </div>

        <div className="flex gap-4">
          {/* フォルダサイドバー */}
          {showFolders && (
            <div className="w-48 border-r pr-4">
              <FolderManager
                generatorType="enemy"
                items={enemies}
                selectedFolderId={selectedFolderId}
                onFolderSelect={setSelectedFolderId}
                draggedItemId={draggedItemId}
                onItemMove={(itemId, toFolderId) => {
                  const currentFolder = getItemFolder('enemy', itemId)
                  moveItemBetweenFolders('enemy', itemId, currentFolder?.id || null, toFolderId)
                }}
              />
            </div>
          )}
          
          {/* エネミーリスト */}
          <div className="flex-1">
            <ScrollArea className="h-[500px] w-full overflow-x-hidden">
              <div className="space-y-2 max-w-full w-full">
                {filteredEnemies.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                {searchTerm ? '検索結果がありません' : '保存済みのエネミーがありません'}
              </p>
            ) : (
              filteredEnemies.map((enemy) => (
                <div 
                  key={enemy.id} 
                  className="card p-2 sm:p-4 overflow-hidden max-w-full w-[calc(100%-2rem)] sm:w-full mr-auto cursor-move hover:shadow-lg transition-shadow"
                  draggable
                  onDragStart={(e) => {
                    setDraggedItemId(enemy.id)
                    e.dataTransfer.effectAllowed = 'move'
                  }}
                  onDragEnd={() => {
                    setDraggedItemId(null)
                  }}
                  style={{
                    opacity: draggedItemId === enemy.id ? 0.5 : 1
                  }}
                >
                  {/* モバイル版: 縦並びレイアウト */}
                  <div className="flex flex-col gap-3 sm:hidden w-full">
                    <div className="w-full">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-sm truncate">{enemy.title}</h3>
                        {enemy.favorite && <Badge variant="secondary" className="text-xs">お気に入り</Badge>}
                      </div>
                      
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1 flex-wrap">
                        <Badge variant="outline" className="text-xs">{enemy.result.basicInfo.type}</Badge>
                        <Badge variant="outline" className="text-xs">{enemy.result.basicInfo.category}</Badge>
                        <Badge variant="destructive" className="text-xs">脅威度: {enemy.result.basicInfo.threat}</Badge>
                      </div>
                      
                      <p className="text-xs text-muted-foreground mt-2 truncate">
                        {enemy.result.basicInfo.summary}
                      </p>
                      
                      {enemy.tags && enemy.tags.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {enemy.tags.slice(0, 2).map((tag, i) => (
                            <span key={i} className="px-2 py-1 bg-primary/10 rounded text-xs">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(enemy.createdAt).toLocaleDateString('ja-JP', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                      
                      {/* フォルダ選択 */}
                      <div className="mt-2">
                        <FolderMoveSelect
                          generatorType="enemy"
                          itemId={enemy.id}
                          currentFolderId={getItemFolder('enemy', enemy.id)?.id}
                          onMove={() => {}}
                        />
                      </div>
                    </div>
                    
                    {/* モバイル版: 横一列のボタン */}
                    <div className="flex gap-0.5 justify-start w-full">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onLoad({ input: enemy.input, result: enemy.result })}
                        title="読込"
                        className="w-12 h-7 px-1"
                      >
                        <Eye className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingEnemy(enemy)}
                        title="編集"
                        className="w-12 h-7 px-1"
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      {onPartialRegenerate && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onPartialRegenerate({ input: enemy.input, result: enemy.result })}
                          title="部分再生成"
                          className="w-12 h-7 px-1"
                        >
                          <RefreshCw className="h-3 w-3" />
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportSingleAsText(enemy.id)}
                        title="テキスト形式でエクスポート"
                        className="w-14 h-7 px-0.5 text-[9px]"
                      >
                        <Download className="h-2.5 w-2.5" />
                        <span className="ml-0.5">txt</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportSingleAsJSON(enemy.id)}
                        title="JSON形式でエクスポート"
                        className="w-14 h-7 px-0.5 text-[9px]"
                      >
                        <Download className="h-2.5 w-2.5" />
                        <span className="ml-0.5">json</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (confirm('このエネミーを削除しますか？')) {
                            removeEnemy(enemy.id)
                          }
                        }}
                        className="w-12 h-7 px-1"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* PC版: 既存の横並びレイアウト */}
                  <div className="hidden sm:flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-sm truncate">{enemy.title}</h3>
                        {enemy.favorite && <Badge variant="secondary">お気に入り</Badge>}
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                        <Badge variant="outline">{enemy.result.basicInfo.type}</Badge>
                        <Badge variant="outline">{enemy.result.basicInfo.category}</Badge>
                        <Badge variant="destructive">脅威度: {enemy.result.basicInfo.threat}</Badge>
                      </div>
                      
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                        {enemy.result.basicInfo.summary}
                      </p>
                      
                      {enemy.tags && enemy.tags.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {enemy.tags.slice(0, 2).map((tag, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(enemy.createdAt).toLocaleDateString('ja-JP', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                      
                      {/* フォルダ選択（PC版） */}
                      <div className="mt-2 w-32">
                        <FolderMoveSelect
                          generatorType="enemy"
                          itemId={enemy.id}
                          currentFolderId={getItemFolder('enemy', enemy.id)?.id}
                          onMove={() => {}}
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onLoad({ input: enemy.input, result: enemy.result })}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          読込
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => exportSingleAsText(enemy.id)}
                          title="テキスト形式でエクスポート"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          txt
                        </Button>
                      </div>
                      
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingEnemy(enemy)}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          編集
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => exportSingleAsJSON(enemy.id)}
                          title="JSON形式でエクスポート"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          json
                        </Button>
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (confirm('このエネミーを削除しますか？')) {
                            removeEnemy(enemy.id)
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
              </div>
            </ScrollArea>
          </div>
        </div>

        {enemies.length >= 450 && (
          <p className="text-xs text-muted-foreground text-center">
            保存可能件数の上限（500件）に近づいています
          </p>
        )}
      </div>
      
      {editingEnemy && (
        <EnemyEditModal
          isOpen={!!editingEnemy}
          onClose={() => setEditingEnemy(null)}
          onSave={(updatedResult) => {
            updateEnemy(editingEnemy.id, {
              result: { ...editingEnemy.result, ...updatedResult },
              updatedAt: new Date().toISOString()
            })
            setEditingEnemy(null)
          }}
          enemyResult={editingEnemy.result}
          title={`「${editingEnemy.title}」を編集`}
        />
      )}
    </div>
  )
}